// project imports
// action - state management
import { CUMULATIVE_INSTALLS_REDUCER_KEY, NEW_INSTALLS_REDUCER_KEY } from "..";
import * as actionTypes from "../../actions/actionTypes";
import { NEW_INSTALLS_KEY } from "./newInstallsReducer";
import { itemType } from "../types";
import { years } from "store/utils/years";
import {
  churnMonth,
  cumulativeInstallsMonths,
  installMonths,
} from "./staticYearsData";

// This is to filter based on company types
import { SaaSCoType, GeneralCoType } from "constants/companyTypes";

// subscriber acquisition assumption' keys
export const CUMULATIVE_INSTALLS_KEY = "cumulativeInstalls";
export const INSTALL_KEY = "install";
export const UNINSTALL_KEY = "uninstall";
export const TOTAL_KEY = "total";
export const CHURN_KEY = "churn";
export const UNINSTALL_CHURN_KEY = "uninstallChurn";

export const initialState = {
  [CUMULATIVE_INSTALLS_KEY]: {
    title: "Cumulative App Installs",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      if (yearKey === 1) {
        return getReducerFunc(CUMULATIVE_INSTALLS_REDUCER_KEY)[TOTAL_KEY][
          "initialPeriod"
        ];
      }
      return getReducerFunc(CUMULATIVE_INSTALLS_REDUCER_KEY)[
        TOTAL_KEY
      ].calculate(yearKey - 1, getReducerFunc);
    },
    months: cumulativeInstallsMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [INSTALL_KEY]: {
    title: "Install",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    showInitialPeriod: true,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return getReducerFunc(NEW_INSTALLS_REDUCER_KEY)[
        NEW_INSTALLS_KEY
      ].calculate(yearKey, getReducerFunc, companyType);
    },
    months: installMonths,

    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [UNINSTALL_KEY]: {
    title: "Uninstall",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    showInitialPeriod: true,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      let totalResult = getReducerFunc(CUMULATIVE_INSTALLS_REDUCER_KEY)[
        TOTAL_KEY
      ].calculate(yearKey - 1, getReducerFunc);
      if (yearKey === 1) {
        totalResult = getReducerFunc(CUMULATIVE_INSTALLS_REDUCER_KEY)[
          TOTAL_KEY
        ]["initialPeriod"];
      }
      let result =
        (getReducerFunc(CUMULATIVE_INSTALLS_REDUCER_KEY)[CHURN_KEY]["years"][
          yearKey
        ] /
          100) *
        totalResult;
      return result === 0 ? 0 : (result * -1).toFixed(0);
    },
    months: cumulativeInstallsMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [TOTAL_KEY]: {
    title: "Total",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    showInitialPeriod: true,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      let previousTotal = getReducerFunc(CUMULATIVE_INSTALLS_REDUCER_KEY)[
        TOTAL_KEY
      ]["initialPeriod"];

      if (yearKey > 1) {
        previousTotal = getReducerFunc(CUMULATIVE_INSTALLS_REDUCER_KEY)[
          TOTAL_KEY
        ].calculate(yearKey - 1, getReducerFunc);
      }

      const installs = getReducerFunc(CUMULATIVE_INSTALLS_REDUCER_KEY)[
        INSTALL_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const churn = getReducerFunc(CUMULATIVE_INSTALLS_REDUCER_KEY)[CHURN_KEY][
        "years"
      ][yearKey];

      const result =
        Number(previousTotal) +
        Number(installs) +
        Number(((previousTotal * churn) / 100) * -1);

      return result.toFixed(0);
    },
    months: cumulativeInstallsMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [CHURN_KEY]: {
    title: "Churn from previous year",
    unit: "PERCENTAGE",
    unitSymbol: "%",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [SaaSCoType, GeneralCoType],
    // FIXME: Why is there no calculate function?
    months: churnMonth,
    years: {
      [years.YEAR_1_KEY]: 20,
      [years.YEAR_2_KEY]: 20,
      [years.YEAR_3_KEY]: 20,
      [years.YEAR_4_KEY]: 20,
      [years.YEAR_5_KEY]: 20,
    },
  },
  [UNINSTALL_CHURN_KEY]: {
    title: "Uninstall",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      let totalResult = getReducerFunc(CUMULATIVE_INSTALLS_REDUCER_KEY)[
        TOTAL_KEY
      ]["initialPeriod"];
      if (yearKey > 1) {
        totalResult = getReducerFunc(CUMULATIVE_INSTALLS_REDUCER_KEY)[
          TOTAL_KEY
        ].calculate(yearKey - 1, getReducerFunc);
      }

      const result =
        (getReducerFunc(CUMULATIVE_INSTALLS_REDUCER_KEY)[CHURN_KEY]["years"][
          yearKey
        ] /
          100) *
        totalResult;

      return result.toFixed(0);
    },
    months: cumulativeInstallsMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
};

// ==============================|| SUBSCRIPTION ACQUISITION ASSUMPTIONS REDUCER ||============================== //

const cumulativeInstallsReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.SET_CUMULATIVE_INSTALL_BY_YEAR:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          years: {
            ...state[action.key].years,
            [action.yearKey]: [action.amount],
          },
        },
      };
    case actionTypes.SET_CUMULATIVE_INSTALL_BY_MONTH:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          months: {
            ...state[action.key].months,
            [action.yearKey]: [action.amount],
          },
        },
      };
    case actionTypes.REPLACE_CUMULATIVE_INSTALL_YEARS:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          months: action?.payload,
        },
      };
    case actionTypes.RESET_CUMULATIVE_INSTAL_REDUCER:
      return initialState;
    default:
      return state;
  }
};

export default cumulativeInstallsReducer;
